import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, map, Observable, throttleTime } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';


@Injectable({
  providedIn: 'root'
})
export class MuiStickyService implements OnDestroy {

  readonly offset$: Observable<number>;

  private readonly _offset = new BehaviorSubject(0);
  private readonly _stackedElements$ = new BehaviorSubject<HTMLElement[]>([]);
  private readonly _resizeObserver = new ResizeObserver(([entry]) => {
    // Update offset when the element is resized
    this.updateOffset(entry.contentRect);
  });
  private readonly _intersectionObserver = new IntersectionObserver(([entry]) => {
    // Update offset when the element is fully visible or not at all
    this.updateOffset(entry.intersectionRect);
  }, {
    threshold: [0, 1] // Trigger when the element is fully visible or not at all
  });

  constructor() {
    this.offset$ = this._offset.asObservable().pipe(
      distinctUntilChanged(),
      throttleTime(16, undefined, { leading: true, trailing: true }) // ~60fps
    );
  }

  ngOnDestroy() {
    this._resizeObserver.disconnect();
    this._intersectionObserver.disconnect();
  }

  addToStack(element: HTMLElement): void {
    const current = this._stackedElements$.getValue();
    if (current.includes(element)) {
      return;
    }
    this._stackedElements$.next([...current, element]);
  }

  removeFromStack(element: HTMLElement): void {
    const current = this._stackedElements$.getValue();
    const index = current.indexOf(element);
    if (index === -1) {
      return;
    }
    current.splice(index, 1);
    this._stackedElements$.next(current);
  }

  /**
   * Get the dynamic top offset for a given element. The offset is calculated by adding the offset anchor height and stacked
   * elements above the given element.
   *
   * @param {HTMLElement} element - The element to get the offset for.
   * @returns {Observable<number>} - An observable that emits the offset value.
   */
  getOffsetForElement(element: HTMLElement): Observable<number> {
    const stackOffset$: Observable<number> = this._stackedElements$.pipe(
      map((stackedElements) => {
        // Find position of element in the stack. If it is found, return the offset plus all the heights of the elements above it.
        // If it is not found, return the offset plus the heights of all stacked elements.
        const index = stackedElements.indexOf(element);
        const elementsAbove = (index >= 0) ? stackedElements.slice(0, index) : stackedElements;
        const heights = elementsAbove.map(el => el.getBoundingClientRect().height);
        return heights.reduce((acc, height) => acc + height, 0);
      })
    );

    return combineLatest([
      this.offset$,
      stackOffset$
    ]).pipe(
      map(([offset, stackedOffset]) => offset + stackedOffset)
    );
  }

  setOffsetAnchor(element: HTMLElement): void {
    this.updateOffset(element.getBoundingClientRect());
    this._resizeObserver.observe(element, { box: 'border-box' });
    this._intersectionObserver.observe(element);
  }

  removeOffsetAnchor(element: HTMLElement): void {
    this._resizeObserver.unobserve(element);
    this._intersectionObserver.unobserve(element);
  }

  private updateOffset(rect: DOMRect): void {
    const { top, height } = rect;
    this._offset.next(top + height);
  }
}
